<script lang="ts">
  import type { LicenseInfo } from '$lib/types/subscription';
  import { SubscriptionService } from '$lib/services/subscription/SubscriptionService';
  import { subscriptionStore } from '$lib/stores/subscription';
  import { useError } from '$lib/hooks/useError';
  import { errorService } from '$lib/services/ErrorService';
  
  export let licenseInfo: LicenseInfo | null;
  export let onDeactivate: () => void;
  
  // Error handling setup
  const { tryExecute } = useError('ActiveLicenseDisplay');
  
  let isDeactivating = false;
  
  async function handleDeactivate() {
    if (!confirm('정말로 라이센스를 비활성화하시겠습니까?\n\n이 디바이스에서 라이센스가 제거되며, 다른 디바이스에서 사용할 수 있게 됩니다.')) {
      return;
    }
    
    isDeactivating = true;
    
    const [result, error] = await tryExecute(
      async () => {
        const service = SubscriptionService.getInstance();

        // Deactivate license
        await service.deactivateLicense();

        // Refresh subscription status to reflect changes
        await subscriptionStore.refresh();

        errorService.showSuccess('라이센스가 성공적으로 비활성화되었습니다.');
        onDeactivate();
        return true;
      },
      {
        severity: 'error',
        context: { action: 'deactivateLicense' },
        showToUser: false
      }
    );
    
    if (error) {
      errorService.handleApiError(error, { component: 'ActiveLicenseDisplay', action: 'deactivateLicense' });
      errorService.showWarning('라이센스 비활성화에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }
    
    isDeactivating = false;
  }
  
  function formatLicenseKey(key: string): string {
    if (!key) return '';
    return `${key.slice(0, 8)}...${key.slice(-4)}`;
  }
  
  function formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('ko-KR');
    } catch (error) {
      // Silent fail for date formatting
      return '';
    }
  }
  
  function getSubscriptionType(licenseInfo: LicenseInfo | null): string {
    if (!licenseInfo) return '';

    // BETA 라이센스
    if (isBetaLicense(licenseInfo)) {
      return '베타 테스트 프로그램';
    }

    if (licenseInfo.metadata?.price !== undefined) {
      const price = Number(licenseInfo.metadata.price);
      if (price >= 2999) {
        return `연간 구독 ($${(price / 100).toFixed(2)})`;
      } else {
        return `월간 구독 ($${(price / 100).toFixed(2)})`;
      }
    } else if (licenseInfo.subscriptionId) {
      return '구독 플랜';
    }
    return '';
  }
  
  function getDeviceUsage(licenseInfo: LicenseInfo | null): string {
    if (!licenseInfo || !licenseInfo.maxDevices) return '';

    if (licenseInfo.metadata?.activeDeviceCount !== undefined) {
      return `${licenseInfo.metadata.activeDeviceCount} / ${licenseInfo.maxDevices} 사용 중`;
    } else if (licenseInfo.devices?.length) {
      return `${licenseInfo.devices.length} / ${licenseInfo.maxDevices} 사용 중`;
    } else if (licenseInfo.metadata?.uses !== undefined) {
      return `${licenseInfo.metadata.uses} / ${licenseInfo.maxDevices} 사용 중 (총 활성화)`;
    } else {
      return `1 / ${licenseInfo.maxDevices} 사용 중 (현재 디바이스)`;
    }
  }

  function isBetaLicense(licenseInfo: LicenseInfo | null): boolean {
    return licenseInfo?.licenseKey?.toUpperCase().startsWith('BETA-') || false;
  }
</script>

<div class="active-license">
  <div class="license-status">
    <div class="status-icon success">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" fill="currentColor"/>
      </svg>
    </div>
    <div class="status-content">
      <h4 class="status-title">
        {#if isBetaLicense(licenseInfo)}
          베타 라이센스 활성화됨
        {:else}
          라이센스 활성화됨
        {/if}
      </h4>
      <p class="status-description">
        {#if isBetaLicense(licenseInfo)}
          베타 테스터로서 확장된 기능을 사용하실 수 있습니다
        {:else}
          프리미엄 기능을 모두 사용할 수 있습니다
        {/if}
      </p>
    </div>
  </div>
  
  <div class="license-details">
    {#if licenseInfo?.licenseKey}
      <div class="detail-item">
        <span class="detail-label">라이센스 키</span>
        <span class="detail-value mono">{formatLicenseKey(licenseInfo.licenseKey)}</span>
      </div>
    {/if}
    
    {#if licenseInfo?.email}
      <div class="detail-item">
        <span class="detail-label">등록 이메일</span>
        <span class="detail-value">{licenseInfo.email}</span>
      </div>
    {/if}
    
    {#if licenseInfo?.purchaseDate}
      <div class="detail-item">
        <span class="detail-label">구매일</span>
        <span class="detail-value">{formatDate(licenseInfo.purchaseDate)}</span>
      </div>
    {/if}
    
    {#if licenseInfo?.productName}
      <div class="detail-item">
        <span class="detail-label">제품명</span>
        <span class="detail-value">{licenseInfo.productName}</span>
      </div>
    {/if}
    
    {#if getSubscriptionType(licenseInfo)}
      <div class="detail-item">
        <span class="detail-label">
          {#if isBetaLicense(licenseInfo)}
            라이센스 유형
          {:else}
            구독 유형
          {/if}
        </span>
        <span class="detail-value">{getSubscriptionType(licenseInfo)}</span>
      </div>
    {/if}
    
    {#if licenseInfo?.expiresAt}
      <div class="detail-item">
        <span class="detail-label">만료 예정일</span>
        <span class="detail-value">{formatDate(licenseInfo.expiresAt)}</span>
      </div>
    {/if}
    
    {#if licenseInfo?.maxDevices}
      <div class="detail-item">
        <span class="detail-label">디바이스 제한</span>
        <span class="detail-value">{getDeviceUsage(licenseInfo)}</span>
      </div>
    {/if}
  </div>
  
  <button 
    class="deactivate-button" 
    on:click={handleDeactivate}
    disabled={isDeactivating}
  >
    {isDeactivating ? '비활성화 중...' : '라이센스 비활성화'}
  </button>
</div>

<style>
  .active-license {
    padding: 20px;
    background: linear-gradient(135deg, #f0f4ff 0%, #f9f5ff 100%);
    border: 1px solid #c7d2fe;
    border-radius: 12px;
  }
  
  .license-status {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
  }
  
  .status-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  
  .status-icon.success {
    background: #dcfce7;
    color: #16a34a;
  }
  
  .status-content h4 {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 4px 0;
  }
  
  .status-content p {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
  }
  
  .license-details {
    display: grid;
    gap: 12px;
    margin-bottom: 20px;
  }
  
  .detail-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #e0e7ff;
  }
  
  .detail-item:last-child {
    border-bottom: none;
  }
  
  .detail-label {
    font-size: 13px;
    color: #6b7280;
  }
  
  .detail-value {
    font-size: 13px;
    color: #111827;
    font-weight: 500;
    text-align: right;
  }
  
  .detail-value.mono {
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace;
  }
  
  .deactivate-button {
    width: 100%;
    padding: 10px;
    background: white;
    color: #dc2626;
    border: 1px solid #fecaca;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .deactivate-button:hover:not(:disabled) {
    background: #fef2f2;
    border-color: #fca5a5;
  }
  
  .deactivate-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  /* Dark mode support */
  @media (prefers-color-scheme: dark) {
    .active-license {
      background: linear-gradient(135deg, #1e293b 0%, #1e1b4b 100%);
      border-color: #4b5563;
    }
    
    .status-icon.success {
      background: #064e3b;
      color: #6ee7b7;
    }
    
    .status-content h4 {
      color: #f3f4f6;
    }
    
    .status-content p {
      color: #9ca3af;
    }
    
    .detail-item {
      border-bottom-color: #374151;
    }
    
    .detail-label {
      color: #9ca3af;
    }
    
    .detail-value {
      color: #f3f4f6;
    }
    
    .deactivate-button {
      background: #1f2937;
      color: #ef4444;
      border-color: #7f1d1d;
    }
    
    .deactivate-button:hover:not(:disabled) {
      background: #7f1d1d;
      border-color: #991b1b;
    }
  }
  
  :global(.dark-theme) .active-license {
    background: linear-gradient(135deg, #1e293b 0%, #1e1b4b 100%);
    border-color: #4b5563;
  }
  
  :global(.dark-theme) .status-icon.success {
    background: #064e3b;
    color: #6ee7b7;
  }
  
  :global(.dark-theme) .status-content h4 {
    color: #f3f4f6;
  }
  
  :global(.dark-theme) .status-content p {
    color: #9ca3af;
  }
  
  :global(.dark-theme) .detail-item {
    border-bottom-color: #374151;
  }
  
  :global(.dark-theme) .detail-label {
    color: #9ca3af;
  }
  
  :global(.dark-theme) .detail-value {
    color: #f3f4f6;
  }
  
  :global(.dark-theme) .deactivate-button {
    background: #1f2937;
    color: #ef4444;
    border-color: #7f1d1d;
  }
  
  :global(.dark-theme) .deactivate-button:hover:not(:disabled) {
    background: #7f1d1d;
    border-color: #991b1b;
  }
</style>